import { useCallback, useMemo } from 'react'
import useMerchandiseCategories from 'hooks/Merchandise/useMerchandiseCategories'
import { arrayToMap } from 'lib/array/arrayUtils'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import FilterChip from 'LuxKit/Chips/FilterChip'

/** Matches MerchandiseOfferCarousel and MerchandiseCategoryPage subcategory thresholds */
export const MIN_PRODUCTS_FOR_POINTS_CATEGORY_PILL = 4

export const MAX_POINTS_CATEGORY_PILLS = 5

interface PillsRowProps {
  categoryIds: Array<string>
  selectedCategoryId: string | null
  onSelectCategory: (categoryId: string | null) => void
  allCategoriesLabel?: string
}

interface CategoryPillProps {
  category: App.MerchandiseCategory
  selected: boolean
  onPillClick: (categoryId: string) => void
}

function MerchandisePointsCategoryPill(props: CategoryPillProps) {
  const { category, selected, onPillClick } = props

  const onClick = useCallback(() => {
    onPillClick(category.id)
  }, [category.id, onPillClick])

  return (
    <FilterChip
      size="medium"
      selected={selected}
      onClick={onClick}
    >
      {category.name}
    </FilterChip>
  )
}

export function MerchandisePointsCarouselCategoryPillsRow(props: PillsRowProps) {
  const { categoryIds, selectedCategoryId, onSelectCategory, allCategoriesLabel } = props

  const [categories, fetchingCategories] = useMerchandiseCategories(
    { categoryIds },
    { disabled: categoryIds.length === 0 },
  )

  const categoryById = useMemo(() => arrayToMap(categories, c => c.id), [categories])

  const allCategoryMetadataLoaded = useMemo(() => {
    if (categoryIds.length === 0) {
      return false
    }
    return categoryIds.every((id) => categoryById.has(id))
  }, [categoryById, categoryIds])

  const onPillClick = useCallback((categoryId: string) => {
    onSelectCategory(selectedCategoryId === categoryId ? null : categoryId)
  }, [onSelectCategory, selectedCategoryId])

  if (categoryIds.length === 0 || fetchingCategories || !allCategoryMetadataLoaded) {
    return null
  }

  return (
    <HorizontalContainerWithFadedOverflow gap={8}>
      {allCategoriesLabel && <FilterChip
        size="medium"
        selected={selectedCategoryId === null}
        onClick={() => onSelectCategory(null)}
      >
        {allCategoriesLabel}
      </FilterChip>}
      {categoryIds.map((id) => {
        const category = categoryById.get(id)
        if (!category) {
          return null
        }

        return (
          <MerchandisePointsCategoryPill
            key={id}
            category={category}
            selected={selectedCategoryId === id}
            onPillClick={onPillClick}
          />
        )
      })}
    </HorizontalContainerWithFadedOverflow>
  )
}
